'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'nextjs-toploader/app';
import { useTranslations } from 'next-intl';
import { Stack, TextInput, PasswordInput, Alert, Anchor, Text } from '@mic-miconnect/lib-ui';
import { loginAction } from '../actions/login.action';
import { AppButton } from '@mic-miconnect/lib-ui';
import { createLoginSchema, type LoginFormValues } from '../types/auth.types';
import { useCurrentUser } from '@shared/contexts/UserContext';
import { ApiErrorCodes } from '@shared/services/api-errors';

interface Props {
  callbackUrl: string;
  sessionExpired?: boolean;
}

export function LoginForm({ callbackUrl, sessionExpired = false }: Props) {
  const router = useRouter();
  const t = useTranslations('auth.login');
  const tErrors = useTranslations('apiErrors');
  const [serverError, setServerError] = useState<string | null>(null);
  const { setUser } = useCurrentUser();

  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (values: LoginFormValues) => {
    setServerError(null);

    const result = await loginAction(values);

    if (!result.success) {
      setServerError(
        result.code === ApiErrorCodes.INVALID_CREDENTIALS ? result.error : tErrors(result.code)
      );
      return;
    }

    setUser(result.user);
    router.push(callbackUrl);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap="md">
        {sessionExpired && (
          <Alert color="yellow" variant="light">
            {tErrors(ApiErrorCodes.UNAUTHORIZED)}
          </Alert>
        )}

        {serverError && (
          <Alert color="red" variant="light">
            {serverError}
          </Alert>
        )}

        <TextInput
          c="white"
          label={t('usernameLabel')}
          autoComplete="username"
          error={errors.username?.message}
          {...register('username')}
        />

        <PasswordInput
          c="white"
          label={t('passwordLabel')}
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <AppButton type="submit" loading={isSubmitting} fullWidth>
          {t('submit')}
        </AppButton>

        <Text ta="center" size="sm">
          <Anchor href="/forgot-password" c="white" size="sm">
            {t('forgotPassword')}
          </Anchor>
        </Text>
      </Stack>
    </form>
  );
}
